import {
  Bold,
  Heading2,
  Heading3,
  Heading4,
  Heading5,
  Italic,
  Link2,
  List,
  ListOrdered,
} from 'lucide-react';
import { useLayoutEffect, useRef, useState } from 'react';

import { Markdown } from '@/components/ui/markdown';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  id?: string;
  'aria-invalid'?: boolean;
  className?: string;
}

type Mode = 'write' | 'preview';

type Tool = { icon: typeof Bold; label: string } & (
  | { kind: 'wrap'; before: string; after: string; placeholder: string }
  | { kind: 'prefix'; prefix: string }
);

// Static toolbar config — no closures over component refs (keeps the
// render-time `react-hooks/refs` rule happy; the textarea ref is read only
// inside the click handler).
const TOOLS: Tool[] = [
  { icon: Bold, label: 'Bold', kind: 'wrap', before: '**', after: '**', placeholder: 'bold text' },
  { icon: Italic, label: 'Italic', kind: 'wrap', before: '_', after: '_', placeholder: 'italic text' },
  { icon: Heading2, label: 'Heading 2', kind: 'prefix', prefix: '## ' },
  { icon: Heading3, label: 'Heading 3', kind: 'prefix', prefix: '### ' },
  { icon: Heading4, label: 'Heading 4', kind: 'prefix', prefix: '#### ' },
  { icon: Heading5, label: 'Heading 5', kind: 'prefix', prefix: '##### ' },
  { icon: List, label: 'Bulleted list', kind: 'prefix', prefix: '- ' },
  { icon: ListOrdered, label: 'Numbered list', kind: 'prefix', prefix: '1. ' },
  { icon: Link2, label: 'Link', kind: 'wrap', before: '[', after: '](https://)', placeholder: 'link text' },
];

/**
 * Lightweight Markdown editor: a textarea with a formatting toolbar and a
 * Write/Preview toggle. Stores plain Markdown; preview renders via {@link Markdown}
 * (no raw HTML executed). Controlled — owns no value state.
 */
export function MarkdownEditor({
  value,
  onChange,
  placeholder,
  rows = 18,
  id,
  className,
  ...aria
}: MarkdownEditorProps) {
  const [mode, setMode] = useState<Mode>('write');
  const ref = useRef<HTMLTextAreaElement>(null);
  // Selection to restore after a toolbar edit re-renders the textarea.
  const pendingSel = useRef<[number, number] | null>(null);

  useLayoutEffect(() => {
    if (pendingSel.current && ref.current) {
      const [start, end] = pendingSel.current;
      ref.current.focus();
      ref.current.setSelectionRange(start, end);
      pendingSel.current = null;
    }
  }, [value]);

  /** Apply a toolbar tool to the current textarea selection (event-time only). */
  function applyTool(tool: Tool) {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    if (tool.kind === 'wrap') {
      const selected = value.slice(start, end) || tool.placeholder;
      const next =
        value.slice(0, start) + tool.before + selected + tool.after + value.slice(end);
      pendingSel.current = [start + tool.before.length, start + tool.before.length + selected.length];
      onChange(next);
    } else {
      const lineStart = value.lastIndexOf('\n', start - 1) + 1;
      const next = value.slice(0, lineStart) + tool.prefix + value.slice(lineStart);
      pendingSel.current = [start + tool.prefix.length, end + tool.prefix.length];
      onChange(next);
    }
  }

  return (
    <div
      className={cn(
        'border-input focus-within:border-ring focus-within:ring-ring/50 overflow-hidden rounded-md border focus-within:ring-[3px]',
        className,
      )}
    >
      <div className="bg-muted/40 flex items-center gap-0.5 border-b px-1 py-1">
        {mode === 'write'
          ? TOOLS.map((tool) => {
              const Icon = tool.icon;
              return (
                <button
                  key={tool.label}
                  type="button"
                  onClick={() => applyTool(tool)}
                  aria-label={tool.label}
                  title={tool.label}
                  className="text-muted-foreground hover:bg-accent hover:text-foreground inline-flex size-7 items-center justify-center rounded"
                >
                  <Icon className="size-4" />
                </button>
              );
            })
          : null}
        <div className="ml-auto inline-flex text-xs">
          {(['write', 'preview'] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              aria-pressed={mode === m}
              className={cn(
                'rounded px-2 py-1 capitalize transition-colors',
                mode === m
                  ? 'bg-background text-foreground font-medium shadow-xs'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {m}
            </button>
          ))}
        </div>
      </div>

      {mode === 'write' ? (
        <Textarea
          ref={ref}
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={rows}
          aria-invalid={aria['aria-invalid']}
          // `field-sizing-fixed` overrides the base `field-sizing-content` so the
          // `rows` count actually drives the height (content can still scroll).
          className="field-sizing-fixed rounded-none border-0 shadow-none focus-visible:ring-0"
        />
      ) : (
        <div className="min-h-24 px-3 py-2">
          {value.trim() ? (
            <Markdown>{value}</Markdown>
          ) : (
            <p className="text-muted-foreground text-sm">Nothing to preview.</p>
          )}
        </div>
      )}
    </div>
  );
}
